<script setup lang="ts">
import type { VoixCheckboxFieldInterface, VoixSelectFieldInterface, VoixTextFieldInterface } from '@voix/types'

const props = defineProps<{
  tabColorClass: {
    type: string
    default: 'bg-glueblue-600'
  }
  fields: {
    specialId: VoixTextFieldInterface
    openLabel: VoixTextFieldInterface
    openOnLoad: VoixCheckboxFieldInterface
    showBookNow: VoixCheckboxFieldInterface
    position: VoixSelectFieldInterface
  }
}>()

defineSlice({
  name: { label: 'Specials Slide Out', group: 'Specials', layouts: ['Atlantisbahamas'] },
  preview: 'SlicesGlueSpecialsSpecialsSlideOut.jpg',
  description: 'Module that slides out and shows the selected offer or newsletter signup',
  tags: ['Specials', 'Slide Out'],
  fields: {
    specialId: { type: 'text', label: 'Special' },
    openLabel: { type: 'text', label: 'Open Label', default: '"Travel Info"' },
    openOnLoad: { type: 'checkbox', label: 'Open on load', default: false },
    showBookNow: { type: 'checkbox', label: 'Show Book Now', default: true },
    position: {
      type: 'select',
      label: 'Position',
      options: {
        bottom: 'Bottom',
        right: 'Right',
      },
      default: 'bottom',
    },
  },
  templates: [{ label: 'Specials Slide Out', fields: { specialId: { value: '465' } } }],
})

const isOpen = ref(false)
const special = ref(null)
const size = ref({
  width: 360,
  height: 560,
})

const desktopContainerStyles = computed(() => {
  const styles: { [key: string]: string } = {
    width: `${size.value.width}px`,
    height: `${size.value.height}px`,
  }

  const translateType = props.fields.position.value === 'right' ? 'translateX' : 'translateY'
  const ammount = props.fields.position.value === 'right' ? size.value.width : size.value.height

  if (isOpen.value === false)
    styles.transform = `${translateType}(${ammount}px)`

  return styles
})

const mobileContainerStyles = computed(() => {
  const styles: { [key: string]: string } = {
    width: `${size.value.width}px`,
    height: `${size.value.height}px`,
  }

  const translateType = props.fields.position.value === 'right' ? 'translateX' : 'translateY'
  const ammount = props.fields.position.value === 'right' ? size.value.width : size.value.height

  if (isOpen.value === false)
    styles.transform = `${translateType}(${ammount}px)`

  return styles
})

function setAutoClose() {
  if (props.fields.openOnLoad.value === true) {
    setTimeout(() => {
      isOpen.value = false
    }, 7000)
  }
}

const { data } = await useVoixAsyncData(`/api/devise/specials/${props.fields.specialId.value}`)
special.value = data.value?.data

const tabColorClassComputed = computed(() => {
  return props.tabColorClass || 'bg-glueblue-600'
})

onMounted(() => {
  if (
    props.fields.openOnLoad.value === true
    && process.client
    && window.innerWidth > 768
  ) {
    isOpen.value = true
  }

  setAutoClose()
})
</script>

<template>
  <div v-if="special" class="h-fit fixed inset-0 z-[9999]">
    <div class="container mx-auto relative">
      <div
        class="md:block fixed ml-32 transform duration-1000 bg-white  pointer-events-auto"
        :class="{
          'right-0 top-16 lg:top-32': fields.position.value === 'right',
          'hidden left-0 bottom-0': fields.position.value === 'bottom',
        }"
        :style="desktopContainerStyles"
      >
        <GlueSpecialsSpecialsLayoutsSmallSpecial
          :special="special"
          :show-book-now="fields.showBookNow.value"
          location="slideOut"
        />

        <div
          class="cursor-pointer absolute px-3 font-sans uppercase text-white text-2xs transform origin-bottom-right  flex items-center justify-between"
          :class="[
            tabColorClassComputed, {
              'rotate-90 -left-[236px] top-[370px]': fields.position.value === 'right',
              'right-0 top-0': fields.position.value === 'bottom',
            }]"
          style="width: 196px; height: 40px; margin-top: -39px"
          @click="isOpen = !isOpen"
        >
          <div>
            <svg
              class="w-4 h-4 transform duration-1000"
              :class="{ 'rotate-180': !isOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </div>
          <div class="whitespace-nowrap">
            {{ isOpen ? 'Collapse' : fields.openLabel.value }}
          </div>
          <div>
            <svg
              class="w-4 h-4 transform duration-1000"
              :class="{ 'rotate-180': !isOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </div>
        </div>
      </div>
      <div
        class="fixed z-[60] left-0 right-0 bottom-0 transform duration-1000 bg-white flex"
        :class="{
          'hidden': fields.position.value === 'right',
          'md:hidden': fields.position.value === 'bottom',
        }"
        :style="mobileContainerStyles"
      >
        <GlueSpecialsSpecialsLayoutsSmallSpecial
          class="w-full"
          :special="special"
          :image-on-top="true"
        />

        <div
          class="cursor-pointer w-full bg-white absolute right-0 top-0 px-6 font-sans uppercase text-glueblue-600 text-xs origin-bottom-right flex items-center justify-between"
          style="height: 48px"
          :class="{ '-mt-8 animate-bounce-slow': !isOpen, '-mt-12': isOpen }"
          @click="isOpen = !isOpen"
        >
          <div class="ml-16">
            <svg
              class="w-4 h-4 transform duration-1000"
              :class="{ 'rotate-180': isOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </div>
          <div class="whitespace-nowrap">
            {{ isOpen ? 'Collapse' : fields.openLabel.value }}
          </div>
          <div class="mr-16">
            <svg
              class="w-4 h-4 transform duration-1000"
              :class="{ 'rotate-180': isOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
